import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { MagnifyingGlass, UploadSimple, Sparkle, Spinner, X, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
    pending: "text-amber-700 bg-amber-50 border-amber-200",
    rejected: "text-red-700 bg-red-50 border-red-200",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${map[status]}`}>
      {status}
    </span>
  );
}

function DeptTile({ testid, active, onClick, initial, name, subtitle, count }) {
  const empty = count === 0 && !active;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      title={subtitle}
      className={`text-left rounded-sm border transition-all overflow-hidden group ${
        active
          ? "bg-navy border-navy shadow-[0_10px_24px_-10px_rgba(11,30,63,0.55)]"
          : empty
            ? "bg-slate-50 border-dashed border-slate-200 hover:border-cyan hover:bg-white"
            : "bg-white border-slate-200 hover:border-cyan hover:shadow-md hover:-translate-y-0.5"
      }`}
    >
      <div className={`px-4 py-3.5 flex items-start gap-3 ${active ? "border-b border-cyan/40" : "border-b border-slate-100"}`}>
        <div
          className={`w-11 h-11 shrink-0 rounded-sm flex items-center justify-center font-display text-xl ${
            active
              ? "bg-cyan text-navy"
              : empty
                ? "bg-white text-slate-400 border border-slate-200 group-hover:text-navy group-hover:border-cyan"
                : "bg-slate-100 text-navy group-hover:bg-cyan/20"
          }`}
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] uppercase tracking-widest ${active ? "text-cyan" : empty ? "text-slate-400" : "text-slate-500"}`}>
            Department
          </div>
          <div className={`text-sm font-semibold leading-tight ${active ? "text-white" : empty ? "text-slate-500" : "text-navy"}`}>
            {name}
          </div>
        </div>
      </div>
      <div className={`px-4 py-2.5 flex items-center justify-between text-[11px] ${active ? "text-white/70" : empty ? "text-slate-400" : "text-slate-500"}`}>
        <span className="truncate mr-2 leading-snug">{empty ? "No documents yet" : subtitle}</span>
        <span
          className={`font-mono font-bold shrink-0 px-1.5 py-0.5 rounded-sm text-[11px] ${
            active
              ? "bg-white text-navy"
              : empty
                ? "bg-white text-slate-400 border border-slate-200"
                : "bg-cyan/15 text-navy"
          }`}
        >
          {count}
        </span>
      </div>
    </button>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [deps, setDeps] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [categoryId, setCategoryId] = useState("all");
  const [departmentId, setDepartmentId] = useState("all");
  const [loading, setLoading] = useState(false);
  const [smart, setSmart] = useState(null); // { results, documents } or null
  const [smartLoading, setSmartLoading] = useState(false);
  const [deptCounts, setDeptCounts] = useState({ __total: 0 }); // { deptId: count, __total: n }

  const loadDeptCounts = async () => {
    try {
      const { data } = await api.get("/documents");
      const map = { __total: data.length };
      for (const doc of data) {
        const id = doc.department_id || "__none";
        map[id] = (map[id] || 0) + 1;
      }
      setDeptCounts(map);
    } catch { /* silent */ }
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (categoryId !== "all") params.category_id = categoryId;
    if (departmentId !== "all") params.department_id = departmentId;
    const { data } = await api.get("/documents", { params });
    setDocs(data);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const [c, d] = await Promise.all([api.get("/categories"), api.get("/departments")]);
      setCats(c.data); setDeps(d.data);
      await load();
      await loadDeptCounts();
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, status, categoryId, departmentId]);

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const depName = (id) => deps.find((d) => d.id === id)?.name || "—";

  const runSmartSearch = async () => {
    if (!q.trim()) return toast.error("Type a question first");
    setSmartLoading(true);
    try {
      const { data } = await api.post("/documents/smart-search", { q });
      setSmart(data);
      if (!data.results?.length) toast.info("Gemini didn't find a strong match — try different words.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : err.message);
    } finally {
      setSmartLoading(false);
    }
  };

  const clearSmart = () => setSmart(null);
  const reasonFor = (id) => smart?.results?.find((r) => r.doc_id === id);

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-caps">Library</div>
          <h2 className="font-display text-3xl text-navy uppercase mt-1">All documents</h2>
        </div>
        <Link to="/upload">
          <button data-testid="upload-cta" className="btn-navy flex items-center gap-2">
            <UploadSimple size={14} /> Upload
          </button>
        </Link>
      </div>

      <div className="card-flat p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="documents-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && e.shiftKey) { e.preventDefault(); runSmartSearch(); } }}
            placeholder="Search titles, tags, and document text… (Shift+Enter for AI smart search)"
            className="pl-9 h-10"
          />
        </div>
        <button
          onClick={runSmartSearch}
          disabled={smartLoading}
          data-testid="smart-search-btn"
          className="btn-cyan flex items-center gap-2 h-10 disabled:opacity-60"
          title="Ask Gemini to find semantically relevant documents"
        >
          {smartLoading ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
          <span className="whitespace-nowrap">{smartLoading ? "Searching…" : "AI Smart Search"}</span>
        </button>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-44 h-10" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={departmentId} onValueChange={setDepartmentId}>
          <SelectTrigger className={`w-full md:w-52 h-10 ${isAdmin ? "hidden" : ""}`} data-testid="filter-department">
            <SelectValue placeholder="Department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {deps.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={categoryId} onValueChange={setCategoryId}>
          <SelectTrigger className="w-full md:w-52 h-10" data-testid="filter-category">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isAdmin && deps.length > 0 && (
        <div data-testid="dept-pills">
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="label-caps flex items-center gap-2">
              <Buildings size={14} className="text-cyan" weight="fill" /> Browse by department
            </div>
            {departmentId !== "all" && (
              <button
                onClick={() => setDepartmentId("all")}
                data-testid="dept-clear-btn"
                className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-navy flex items-center gap-1"
              >
                <X size={11} /> Show all
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            <DeptTile
              testid="dept-pill-all"
              active={departmentId === "all"}
              onClick={() => setDepartmentId("all")}
              initial="◆"
              name="All departments"
              subtitle="Full library"
              count={deptCounts.__total}
            />
            {deps.map((d) => (
              <DeptTile
                key={d.id}
                testid={`dept-pill-${d.id}`}
                active={false}
                onClick={() => navigate(`/departments/${d.id}`)}
                initial={d.name.trim().charAt(0).toUpperCase()}
                name={d.name}
                subtitle={d.description || "—"}
                count={deptCounts[d.id] || 0}
              />
            ))}
          </div>
        </div>
      )}

      {smart && (
        <div className="card-flat p-4 border-l-4 border-cyan" data-testid="smart-results-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-cyan" />
              <div>
                <div className="label-caps text-navy">AI smart search</div>
                <p className="text-xs text-slate-500">Gemini ranked {smart.results?.length || 0} match{smart.results?.length === 1 ? "" : "es"} by relevance.</p>
              </div>
            </div>
            <button onClick={clearSmart} data-testid="smart-clear" className="text-xs text-zinc-500 hover:text-navy flex items-center gap-1">
              <X size={12} /> Clear
            </button>
          </div>
          {smart.results?.length === 0 && <div className="text-sm text-zinc-500">No relevant documents found.</div>}
          <ul className="space-y-3">
            {smart.results?.map((r) => {
              const d = smart.documents?.find((x) => x.id === r.doc_id);
              if (!d) return null;
              return (
                <li key={r.doc_id} data-testid={`smart-hit-${r.doc_id}`} className="border border-zinc-200 rounded-sm p-3 hover:bg-zinc-50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <Link to={`/documents/${d.id}`} className="font-semibold text-navy hover:text-cyan">{d.title}</Link>
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-navy text-white rounded-sm">{r.score}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{depName(d.department_id)} · {catName(d.category_id)} · v{d.current_version}</div>
                  {r.reason && <div className="text-sm text-zinc-700 mt-2">{r.reason}</div>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {(!isAdmin || departmentId !== "all" || smart) && (
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-zinc-200 bg-zinc-50">
              <th className="px-4 py-3 label-caps font-semibold">Title</th>
              <th className="px-4 py-3 label-caps font-semibold">Department</th>
              <th className="px-4 py-3 label-caps font-semibold">Category</th>
              <th className="px-4 py-3 label-caps font-semibold">Version</th>
              <th className="px-4 py-3 label-caps font-semibold">Expiry</th>
              <th className="px-4 py-3 label-caps font-semibold">Status</th>
              <th className="px-4 py-3 label-caps font-semibold">Uploaded</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="p-12 text-center">
                  <div className="inline-flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-sm bg-slate-100 flex items-center justify-center text-navy font-display text-2xl">
                      {isAdmin && departmentId !== "all"
                        ? (depName(departmentId) || "—").trim().charAt(0).toUpperCase()
                        : "◆"}
                    </div>
                    <div className="text-navy font-semibold">
                      {isAdmin && departmentId !== "all"
                        ? `No documents in ${depName(departmentId)} yet`
                        : "No documents match your filters."}
                    </div>
                    {isAdmin && departmentId !== "all" && (
                      <div className="text-xs text-slate-500 max-w-sm">
                        Upload the first document for this department, or clear the filter to browse the full library.
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <Link
                        to="/upload"
                        data-testid="empty-upload-cta"
                        className="text-xs uppercase tracking-widest px-3 py-1.5 bg-navy text-white rounded-sm hover:bg-navy/90 flex items-center gap-1.5"
                      >
                        <UploadSimple size={12} /> Upload
                      </Link>
                      {isAdmin && departmentId !== "all" && (
                        <button
                          type="button"
                          onClick={() => setDepartmentId("all")}
                          className="text-xs uppercase tracking-widest px-3 py-1.5 border border-slate-300 text-navy rounded-sm hover:border-cyan"
                        >
                          Show all
                        </button>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} className="dms-row border-b border-zinc-100 last:border-b-0" data-testid={`doc-row-${d.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/documents/${d.id}`} className="font-semibold text-navy hover:text-cyan transition-colors">
                    {d.title}
                  </Link>
                  {d.tags?.length > 0 && (
                    <div className="text-xs text-zinc-500 mt-0.5">{d.tags.join(" · ")}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-navy font-medium">{depName(d.department_id)}</td>
                <td className="px-4 py-3 text-zinc-700">{catName(d.category_id)}</td>
                <td className="px-4 py-3 font-mono text-zinc-700">v{d.current_version}</td>
                <td className="px-4 py-3 font-mono text-zinc-700">
                  {d.expiry_date ? new Date(d.expiry_date).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                <td className="px-4 py-3 text-zinc-500 text-xs">
                  {new Date(d.created_at).toLocaleDateString()} by {d.created_by_name}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
